"use client"

import { useAtom, useAtomValue } from 'jotai'
import { useRouter, usePathname } from 'next/navigation'
import { pendingContextItemAtom, PendingContextItem, tabDataAtom } from '@sourcepilot/ui/framework/state/session-data';
import { currentProductAtom } from '@/state/rflp-session';
import { ChatTask } from '@sourcepilot/generated/pdm_tasks/api/chattask'

/**
 * Hook for adding context items to chat from anywhere in the app.
 *
 * When addToContext is called:
 * 1. Sets the pending context item in global state (ChatInput will insert @search_path at cursor)
 * 2. Opens the chat sidebar (or creates a new chat if none exists)
 * 3. If creating a new chat and on a product page, attaches the chat to the product
 */
export function useAddToContext() {
  const [, setPendingContext] = useAtom(pendingContextItemAtom)
  const tabs = useAtomValue(tabDataAtom)
  const currentProduct = useAtomValue(currentProductAtom)
  const router = useRouter()
  const pathname = usePathname()

  const addToContext = async (item: PendingContextItem) => {
    // 1. Set pending context - ChatInput will pick this up and insert @search_path at cursor
    setPendingContext(item)

    // 2. Check if chat sidebar is already open
    const searchParams = new URLSearchParams(window.location.search)
    const currentChatId = searchParams.get('chat')

    if (currentChatId) {
      // Chat already open - pending context will be picked up by ChatInput
      return
    }

    // 3. Find most recent chat tab to reuse
    const chatTabs = tabs.filter(t => t.contentType === 'chat')
    if (chatTabs.length > 0) {
      const latestChat = [...chatTabs].sort((a, b) =>
        b.viewed_at.getTime() - a.viewed_at.getTime()
      )[0]
      // Open that chat in sidebar
      router.push(`${pathname}?chat=${latestChat.id}`)
      return
    }

    // 4. No chat exists - create new ChatTask
    try {
      const task = new ChatTask({
        task_name: item.name,
      })
      const savedTask = await task.save()

      if (!savedTask.id) {
        throw new Error('Task created but no ID returned')
      }

      // 5. Attach to current product if we're on a product page
      if (currentProduct?.id) {
        try {
          const editable = savedTask.makeEditable() as any
          editable.currently_attached_to.set(currentProduct)
          await editable.save()
          console.log(`✅ Attached chat ${savedTask.id} to product ${currentProduct.id}`)
        } catch (attachError) {
          // Log but don't fail - chat was created, attachment is optional
          console.warn('Failed to attach chat to product:', attachError)
        }
      }

      // Open the new chat in sidebar
      router.push(`${pathname}?chat=${savedTask.id}`)
    } catch (error) {
      console.error('Failed to create chat task:', error)
    }
  }

  return { addToContext }
}
